/**
 * Payment types.
 *
 * All money is integer **cents** (750000 === $7,500.00), matching Stripe's API
 * and the backend. Never introduce a float amount — formatting to a display
 * string happens only at render time, via `formatCents`.
 */

export type PaymentPurpose = "PAB_ACTIVATION" | "EXPERT_ENROLLMENT";

export type PaymentStatus =
  | "PENDING"
  | "REQUIRES_ACTION"
  | "PROCESSING"
  | "SUCCEEDED"
  | "FAILED"
  | "CANCELED"
  | "REFUNDED"
  | "PARTIALLY_REFUNDED";

/** Statuses where money may still land; the UI keeps polling in these. */
export const OPEN_PAYMENT_STATUSES: PaymentStatus[] = [
  "PENDING",
  "REQUIRES_ACTION",
  "PROCESSING",
];

/**
 * Stable error codes from the backend. The UI switches on these, never on
 * message text — copy changes must not break behaviour.
 */
export type PaymentErrorCode =
  | "CARD_DECLINED"
  | "PAYMENT_AUTH_REQUIRED"
  | "COUPON_INVALID"
  | "COUPON_EXPIRED"
  | "COUPON_EXHAUSTED"
  | "COUPON_ALREADY_REDEEMED"
  | "COUPON_NOT_APPLICABLE"
  | "COUPON_RATE_LIMITED"
  | "SPONSORSHIP_CODE_INVALID"
  | "SPONSORSHIP_RATE_LIMITED"
  | "PAYMENT_REQUIRED"
  | "PAYMENT_ALREADY_COMPLETED"
  | "PAYMENT_NOT_FOUND"
  | "AMOUNT_MISMATCH"
  | "STRIPE_UNAVAILABLE"
  | "PAYMENTS_DISABLED"
  | "PRICE_NOT_CONFIGURED"
  /**
   * Checkout is paused after a sustained run of declined attempts.
   *
   * Not a punishment and not a decline: retrying a card too fast makes Stripe
   * rate-limit the intent, which comes back as an opaque non-card error the UI
   * can only describe vaguely. Pausing turns that into a clear, timed message.
   * The response carries `retryAfterSeconds` so we can show a live countdown
   * rather than a dead button.
   */
  | "PAYMENT_COOLDOWN";

/**
 * Seconds until a PAYMENT_COOLDOWN lifts, or null if this isn't one.
 *
 * Reads `error_code` (snake_case) because the payment exception handler writes
 * the error envelope by hand rather than through CamelModel — same field
 * `paymentErrorCode()` reads. `retryAfterSeconds` IS camelCase; the handler
 * sets that key explicitly. Falls back to 15 minutes, matching the backend
 * window, so a missing field can never yield a countdown of NaN.
 */
export const cooldownSecondsFrom = (err: unknown): number | null => {
  const data = (err as { response?: { data?: { error_code?: string; retryAfterSeconds?: number } } })
    ?.response?.data;
  if (data?.error_code !== "PAYMENT_COOLDOWN") return null;
  return typeof data.retryAfterSeconds === "number" ? data.retryAfterSeconds : 900;
};

export interface PaymentConfig {
  paymentsEnabled: boolean;
  publishableKey: string | null;
}

/** A priced order summary. Read-only — requesting one reserves nothing. */
export interface Quote {
  planCode: string;
  displayName: string;
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  currency: string;
  couponCode: string | null;
  /**
   * Set when an organisation sponsorship covers this order, so the summary can
   * name who is paying rather than showing a bare discount line.
   */
  sponsorName?: string | null;
}

export interface CouponValidation {
  valid: boolean;
  code: string;
  discountCents: number;
  totalCents: number;
  currency: string;
  description: string | null;
}

/** Result of validating an organisation sponsorship code. Grants nothing. */
export interface SponsorshipValidation {
  valid: boolean;
  code: string;
  /** The sponsoring organisation, shown back so the user can confirm it. */
  companyName: string;
  discountCents: number;
  totalCents: number;
  currency: string;
}

export interface PaymentIntentResult {
  paymentExternalId: string;
  /** Absent for zero-amount (100%-off) payments, which never reach Stripe. */
  clientSecret: string | null;
  status: PaymentStatus;
  amountCents: number;
  subtotalCents: number;
  discountCents: number;
  currency: string;
  couponCode: string | null;
  publishableKey: string | null;
}

export interface PaymentStatusResult {
  paymentExternalId: string;
  status: PaymentStatus;
  amountCents: number;
  currency: string;
  programId: number | null;
  purpose: PaymentPurpose;
  failureCode: string | null;
  succeededAt: string | null;
  paymentMethodBrand: string | null;
  paymentMethodLast4: string | null;
}

export interface PendingPayment {
  paymentExternalId: string;
  purpose: PaymentPurpose;
  programId: number | null;
  status: PaymentStatus;
  amountCents: number;
  currency: string;
  expiresAt: string | null;
  /** Server-decided destination for finishing this payment. */
  resumeUrl: string;
}

export interface Entitlement {
  programId: number;
  /**
   * The program's PUBLIC external_id. Public program payloads carry only this,
   * never the numeric id, so it is what lets a page ask "am I already
   * enrolled in the program I'm looking at?".
   */
  programExternalId?: string | null;
  status: "ACTIVE" | "REVOKED";
  source: "PAYMENT" | "ORGANIZATION" | "COMP" | "COUPON_FULL";
  grantedAt: string | null;
  expiresAt: string | null;
}

/**
 * Drives resume routing. Read from the server rather than inferred locally, so
 * an interrupted payment is recoverable on any device and survives a tab that
 * closed mid-3DS.
 */
export interface MyPaymentsResponse {
  pendingPayments: PendingPayment[];
  entitlements: Entitlement[];
  /**
   * Whether this user would have to pay to activate a PAB.
   *
   * Distinct from `pendingPayments` on purpose: a pending payment only exists
   * once checkout has started, so the wizard cannot use it to decide whether
   * to show the Payment step (no row → step hidden → step unreachable → no row
   * ever created). This field answers the question before any payment exists.
   */
  pabRequiresPayment: boolean;
}

/** Format integer cents for display: 750000 → "$7,500.00". */
export function formatCents(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}
